import { useAppDispatch } from '@/app/hooks';
import { useDeleteFileMutation } from '@/features/api/endpoints/fileEndpoints';
import type { DirectusFiles } from '@/models/types';
import { ActionIcon, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { removeSelectedItemId } from '../../../features/drive/driveSlice';

type DeleteFileBtnProps = {
  file: DirectusFiles;
};

const DeleteFileBtn = ({ file }: DeleteFileBtnProps) => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const [deleteFile] = useDeleteFileMutation();

  const handleDeleteFile = () => {
    modals.openConfirmModal({
      title: t('file.delete.confirm'),
      children: <Text size="sm">{t('file.delete.message')}</Text>,
      labels: {
        cancel: t('common.cancel'),
        confirm: t('common.delete'),
      },
      confirmProps: { color: 'red' },
      centered: true,
      onConfirm: async () => {
        try {
          await deleteFile(file.id).unwrap();
          dispatch(removeSelectedItemId(file.id));
          notifications.show({
            color: 'green',
            message: t('file.delete.success'),
          });
        } catch {
          notifications.show({
            color: 'red',
            message: t('file.delete.error'),
          });
        }
      },
    });
  };

  return (
    <Tooltip label={t('file.delete.confirm')} withArrow>
      <ActionIcon
        variant="subtle"
        color="red"
        onClick={handleDeleteFile}
        aria-label={t('file.delete.confirm')}
      >
        <IconTrash size={18} />
      </ActionIcon>
    </Tooltip>
  );
};

export default DeleteFileBtn;
